/**
 * GSPro Course Builder — Phase 2: canvas, toolbar, course model.
 */

import {
  createCourse,
  PAR_DIMENSIONS,
  getHolePar,
  setHolePar,
  applyDimensionsFromPars,
  addSurfaceShape,
  addTee,
  addTree,
  addHouse,
  addElevationInRegion,
  removeSurfaceShape,
  removeTee,
  removeTree,
  removeHouse,
  addPin,
  addTeePosition,
  addMarker150,
  removePin,
  removePinsForHole,
  removeTeePosition,
  removeMarker150,
  movePin,
  moveTeePosition,
  moveMarker150,
  moveTree,
  moveHouse,
  moveTee,
  moveSurfaceShape,
  sendSurfaceShapeToBack,
  sendTeeToBack,
  sendTreeToBack,
  sendHouseToBack,
  sendPinToBack,
  sendTeePositionToBack,
  sendMarker150ToBack,
  toJSON,
  fromJSON,
  touchMetadata,
  onMetadataTouched,
} from './courseState';
import { createCanvasView } from './canvasView';
import { createToolbar } from './toolbar';
import { createView3d, type View3d } from './view3d';
import { initClipper } from './clipperOffset';
import { randomizeHole, randomizeAllAsync } from './randomizeHole';
import { pointInPolygon, smoothPolyline } from './strokeToPolygon';
import type { Course, Point, House, Marker150, Pin, SurfaceShape, Tee, TeePosition, Tree } from './types';
import { isSurfaceTool, isPenTool, SURFACE_KEYS, type SelectionHit, type SurfaceKey, type ToolId } from './types';
import {
  getDefaultLayerVisibility,
  getLayerForFeature,
  getLayerForSurfaceKey,
  type LayerId,
  type LayerVisibility,
} from './layers';

const YARD_TO_MM = 914.4;
const ELEVATION_NORMALIZED_PER_INCH = 0.05 / 6; // 6 in = 0.05 normalized (0–1 grid)

let course: Course = createCourse(
  PAR_DIMENSIONS[4].widthYd * YARD_TO_MM,
  PAR_DIMENSIONS[4].heightYd * YARD_TO_MM
);
let canvasView: ReturnType<typeof createCanvasView> | null = null;
let toolbar: ReturnType<typeof createToolbar> | null = null;
let view3d: View3d | null = null;
let currentHole = 1;
let selectedLayer: LayerId = 'green';
let layerVisibility: LayerVisibility = getDefaultLayerVisibility();
let showGrid = true;
let syncSaveButtonState: (() => void) | null = null;

let strokePoints: Point[] = [];
let isDrawing = false;
let measurementReadout: HTMLElement | null = null;

/** Selected item (Pointer tool click). Drawn with dashed white outline. */
let selectedHit: SelectionHit | null = null;

/** Hovered item (Pointer tool); draw light gray dashed outline. */
let hoverHit: SelectionHit | null = null;

/** Move tool: item being dragged, or null. */
let moveDragHit: HitResult | null = null;
let moveLastPoint: Point | null = null;

type VertexHit =
  | { type: 'surface'; key: SurfaceKey; shapeIndex: number; vertexIndex: number }
  | { type: 'tee'; teeIndex: number; vertexIndex: number };
const VERTEX_PICK_RADIUS_MM = 1800;

/** Reshape tool: vertex being dragged, or null. */
let reshapeDragVertex: VertexHit | null = null;
let reshapeLastPoint: Point | null = null;

/** Measure tool: 0=idle, 1=one point (live to mouse), 2=two points (live segment 2), 3=locked display */
let measurePhase: 0 | 1 | 2 | 3 = 0;
let measureAnchor1: Point | null = null;
let measureAnchor2: Point | null = null;
let measureSegment2End: Point | null = null;
let measureLivePoint: Point | null = null;

type UndoAction =
  | { type: 'surface'; key: SurfaceKey; index: number }
  | { type: 'tee'; index: number }
  | { type: 'tree'; index: number }
  | { type: 'house'; index: number }
  | { type: 'pin'; index: number }
  | { type: 'teePosition'; index: number }
  | { type: 'marker150'; index: number }
  | { type: 'cutTree'; data: Tree }
  | { type: 'cutHouse'; data: House }
  | { type: 'cutTee'; data: Tee }
  | { type: 'cutPin'; data: Pin }
  | { type: 'cutTeePosition'; data: TeePosition }
  | { type: 'cutMarker150'; data: Marker150 }
  | { type: 'cutSurface'; key: SurfaceKey; data: SurfaceShape };
const undoStack: UndoAction[] = [];

type ClipboardItem =
  | { type: 'tree'; data: Tree }
  | { type: 'house'; data: House }
  | { type: 'tee'; data: Tee }
  | { type: 'pin'; data: Pin }
  | { type: 'teePosition'; data: TeePosition }
  | { type: 'marker150'; data: Marker150 }
  | { type: 'surface'; key: SurfaceKey; data: SurfaceShape };
let clipboard: ClipboardItem | null = null;

function performUndo(): void {
  const action = undoStack.pop();
  if (!action) return;
  if (action.type === 'surface') {
    const list = course.surfaces[action.key];
    if (action.index >= 0 && action.index < (list?.length ?? 0)) {
      const shape = list[action.index];
      if (action.key === 'green' && shape) {
        const holeNum = shape.holeNumber ?? 1;
        removePinsForHole(course, holeNum);
      }
      removeSurfaceShape(course, action.key, action.index);
    }
  } else if (action.type === 'tee') {
    if (action.index >= 0 && action.index < course.tees.length) {
      removeTee(course, action.index);
    }
  } else if (action.type === 'tree') {
    if (action.index >= 0 && action.index < course.trees.length) {
      removeTree(course, action.index);
    }
  } else if (action.type === 'house') {
    if (action.index >= 0 && action.index < course.houses.length) {
      removeHouse(course, action.index);
    }
  } else if (action.type === 'pin') {
    if (action.index >= 0 && action.index < course.pins.length) {
      removePin(course, action.index);
    }
  } else if (action.type === 'teePosition') {
    if (action.index >= 0 && action.index < course.teePositions.length) {
      removeTeePosition(course, action.index);
    }
  } else if (action.type === 'marker150') {
    if (action.index >= 0 && action.index < course.markers150.length) {
      removeMarker150(course, action.index);
    }
  } else if (action.type === 'cutTree') {
    const d = action.data;
    addTree(course, d.x, d.y, d.type, d.radiusMm, d.holeNumber ?? 1);
  } else if (action.type === 'cutHouse') {
    const d = action.data;
    addHouse(course, d.x, d.y, d.radiusMm, d.holeNumber ?? 1);
  } else if (action.type === 'cutTee') {
    const d = action.data;
    addTee(course, {
      points: d.points.map((p) => [p[0], p[1]] as Point),
      holeNumber: d.holeNumber,
      label: d.label,
      id: d.id,
    });
  } else if (action.type === 'cutPin') {
    const d = action.data;
    addPin(course, { x: d.x, y: d.y, holeNumber: d.holeNumber, day: d.day });
  } else if (action.type === 'cutTeePosition') {
    const d = action.data;
    addTeePosition(course, { x: d.x, y: d.y, holeNumber: d.holeNumber, color: d.color });
  } else if (action.type === 'cutMarker150') {
    const d = action.data;
    addMarker150(course, { x: d.x, y: d.y, holeNumber: d.holeNumber });
  } else if (action.type === 'cutSurface') {
    const d = action.data;
    addSurfaceShape(course, action.key, {
      type: d.type,
      points: d.points.map((p) => [p[0], p[1]] as Point),
      holeNumber: d.holeNumber,
    });
  }
  redraw();
  toolbar?.refreshForHoleChange?.();
}

function copyHitToClipboard(hit: SelectionHit): void {
  if (hit.type === 'tree') {
    const t = course.trees[hit.index];
    if (t) clipboard = { type: 'tree', data: { ...t } };
  } else if (hit.type === 'house') {
    const h = course.houses[hit.index];
    if (h) clipboard = { type: 'house', data: { ...h } };
  } else if (hit.type === 'tee') {
    const tee = course.tees[hit.index];
    if (tee?.points) clipboard = { type: 'tee', data: { ...tee, points: tee.points.map((p) => [p[0], p[1]] as Point) } };
  } else if (hit.type === 'pin') {
    const p = course.pins[hit.index];
    if (p) clipboard = { type: 'pin', data: { ...p } };
  } else if (hit.type === 'teePosition') {
    const pos = course.teePositions[hit.index];
    if (pos) clipboard = { type: 'teePosition', data: { ...pos } };
  } else if (hit.type === 'marker150') {
    const m = course.markers150[hit.index];
    if (m) clipboard = { type: 'marker150', data: { ...m } };
  } else if (hit.type === 'surface' && hit.key !== 'secondCut') {
    const shape = course.surfaces[hit.key]?.[hit.index];
    if (shape?.points)
      clipboard = {
        type: 'surface',
        key: hit.key,
        data: { type: shape.type, points: shape.points.map((p) => [p[0], p[1]] as Point), holeNumber: shape.holeNumber },
      };
  }
}

/** Cut (remove) an item and push undo action so Z can restore it. */
function cutHit(hit: SelectionHit): void {
  copyHitToClipboard(hit);
  if (hit.type === 'tree') {
    const t = course.trees[hit.index];
    if (t) {
      undoStack.push({ type: 'cutTree', data: { ...t } });
      removeTree(course, hit.index);
    }
  } else if (hit.type === 'house') {
    const h = course.houses[hit.index];
    if (h) {
      undoStack.push({ type: 'cutHouse', data: { ...h } });
      removeHouse(course, hit.index);
    }
  } else if (hit.type === 'tee') {
    const tee = course.tees[hit.index];
    if (tee) {
      undoStack.push({ type: 'cutTee', data: { ...tee, points: tee.points.map((p) => [p[0], p[1]] as Point) } });
      removeTee(course, hit.index);
    }
  } else if (hit.type === 'pin') {
    const p = course.pins[hit.index];
    if (p) {
      undoStack.push({ type: 'cutPin', data: { ...p } });
      removePin(course, hit.index);
    }
  } else if (hit.type === 'teePosition') {
    const pos = course.teePositions[hit.index];
    if (pos) {
      undoStack.push({ type: 'cutTeePosition', data: { ...pos } });
      removeTeePosition(course, hit.index);
    }
  } else if (hit.type === 'marker150') {
    const m = course.markers150[hit.index];
    if (m) {
      undoStack.push({ type: 'cutMarker150', data: { ...m } });
      removeMarker150(course, hit.index);
    }
  } else if (hit.type === 'surface' && hit.key !== 'secondCut') {
    const shape = course.surfaces[hit.key]?.[hit.index];
    if (shape) {
      undoStack.push({
        type: 'cutSurface',
        key: hit.key,
        data: { type: shape.type, points: shape.points.map((p) => [p[0], p[1]] as Point), holeNumber: shape.holeNumber },
      });
      if (hit.key === 'green') {
        const holeNum = shape.holeNumber ?? 1;
        removePinsForHole(course, holeNum);
      }
      removeSurfaceShape(course, hit.key, hit.index);
    }
  }
  toolbar?.refreshForHoleChange?.();
}

function getPasteCenter(): Point {
  const par = getHolePar(course, currentHole);
  const { heightYd } = PAR_DIMENSIONS[par];
  const h = heightYd * YARD_TO_MM;
  return [0, h / 2];
}

function pasteFromClipboard(): boolean {
  if (!clipboard) return false;
  const [cx, cy] = getPasteCenter();
  if (clipboard.type === 'tree') {
    addTree(course, cx, cy, clipboard.data.type, clipboard.data.radiusMm, clipboard.data.holeNumber ?? currentHole);
    undoStack.push({ type: 'tree', index: course.trees.length - 1 });
  } else if (clipboard.type === 'house') {
    addHouse(course, cx, cy, clipboard.data.radiusMm, clipboard.data.holeNumber ?? currentHole);
    undoStack.push({ type: 'house', index: course.houses.length - 1 });
  } else if (clipboard.type === 'tee') {
    const cen = polygonCentroid(clipboard.data.points);
    const shiftX = cx - cen[0];
    const shiftY = cy - cen[1];
    const pts = clipboard.data.points.map((p) => [p[0] + shiftX, p[1] + shiftY] as Point);
    addTee(course, { points: pts, holeNumber: clipboard.data.holeNumber ?? currentHole, label: clipboard.data.label });
    undoStack.push({ type: 'tee', index: course.tees.length - 1 });
  } else if (clipboard.type === 'pin') {
    addPin(course, { x: cx, y: cy, holeNumber: currentHole, day: clipboard.data.day });
    undoStack.push({ type: 'pin', index: course.pins.length - 1 });
  } else if (clipboard.type === 'teePosition') {
    addTeePosition(course, { x: cx, y: cy, holeNumber: currentHole, color: clipboard.data.color });
    undoStack.push({ type: 'teePosition', index: course.teePositions.length - 1 });
  } else if (clipboard.type === 'marker150') {
    addMarker150(course, { x: cx, y: cy, holeNumber: currentHole });
    undoStack.push({ type: 'marker150', index: course.markers150.length - 1 });
  } else if (clipboard.type === 'surface') {
    if (clipboard.key === 'green' && hasGreenForCurrentHole()) return false;
    const cen = polygonCentroid(clipboard.data.points);
    const shiftX = cx - cen[0];
    const shiftY = cy - cen[1];
    const pts = clipboard.data.points.map((p) => [p[0] + shiftX, p[1] + shiftY] as Point);
    addSurfaceShape(course, clipboard.key, {
      type: clipboard.data.type ?? 'polygon',
      points: pts,
      holeNumber: clipboard.data.holeNumber ?? currentHole,
    });
    undoStack.push({ type: 'surface', key: clipboard.key, index: course.surfaces[clipboard.key].length - 1 });
  }
  toolbar?.refreshForHoleChange?.();
  return true;
}

function pointInGreen(point: Point, hole: number): boolean {
  const shapes = course.surfaces.green;
  if (!shapes) return false;
  for (const shape of shapes) {
    if (shape.holeNumber != null && shape.holeNumber !== hole) continue;
    if (!shape.points || shape.points.length < 3) continue;
    if (pointInPolygon(point, shape.points)) return true;
  }
  return false;
}

function pointInTeeBox(point: Point, hole: number): boolean {
  for (const tee of course.tees) {
    if (tee.holeNumber !== hole || !tee.points || tee.points.length < 3) continue;
    if (pointInPolygon(point, tee.points)) return true;
  }
  return false;
}

/** Centroid of a polygon (average of vertices). */
function polygonCentroid(points: Point[]): Point {
  if (points.length === 0) return [0, 0];
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / points.length, sy / points.length];
}

/** Green center for a hole (centroid of first green shape for that hole), or null. */
function getGreenCenter(hole: number): Point | null {
  const shapes = course.surfaces.green;
  if (!shapes) return null;
  for (const shape of shapes) {
    if (shape.holeNumber != null && shape.holeNumber !== hole) continue;
    if (!shape.points || shape.points.length < 3) continue;
    return polygonCentroid(shape.points);
  }
  return null;
}

/** True if the current hole already has a green (only 1 green per hole). */
function hasGreenForCurrentHole(): boolean {
  return getGreenCenter(currentHole) != null;
}

/** Tee center for a hole (centroid of first tee box for that hole), or null. */
function getTeeCenter(hole: number): Point | null {
  for (const tee of course.tees) {
    if (tee.holeNumber !== hole || !tee.points || tee.points.length < 3) continue;
    return polygonCentroid(tee.points);
  }
  return null;
}

const MARKER_150_YARDS = 150;

/** Position for the 150 yd marker: 150 yards from green center, toward tee (middle of fairway). Returns null if no green. */
function getMarker150Position(hole: number): Point | null {
  const greenCenter = getGreenCenter(hole);
  if (!greenCenter) return null;
  const teeCenter = getTeeCenter(hole);
  let dx: number;
  let dy: number;
  if (teeCenter) {
    dx = teeCenter[0] - greenCenter[0];
    dy = teeCenter[1] - greenCenter[1];
  } else {
    dx = 0;
    dy = -1;
  }
  const len = Math.hypot(dx, dy) || 1;
  const distMm = MARKER_150_YARDS * YARD_TO_MM;
  return [greenCenter[0] + (dx / len) * distMm, greenCenter[1] + (dy / len) * distMm];
}

function getCourse(): Course {
  return course;
}

function getCurrentHole(): number {
  return currentHole;
}

function getHoleName(hole: number): string {
  return course.metadata.holeNames?.[hole] ?? `Hole ${hole}`;
}

function setHoleName(hole: number, name: string): void {
  if (!course.metadata.holeNames) course.metadata.holeNames = {};
  course.metadata.holeNames[hole] = name;
  touchMetadata(course);
  syncSaveButtonState?.();
}

function redraw(extraPreview?: {
  strokePreviewEnd?: Point;
  measureLivePoint?: Point | null;
}) {
  canvasView?.draw({
    strokePoints,
    activeTool: toolbar?.getActiveTool(),
    selectedHit,
    hoverHit,
    ...extraPreview,
    measureAnchor1,
    measureAnchor2,
    measureSegment2End,
    measurePhase,
    measureLivePoint: extraPreview?.measureLivePoint ?? measureLivePoint,
  });
  view3d?.updateCourse();
}

function clampToBounds([x, y]: Point): Point {
  const par = getHolePar(course, currentHole);
  const { widthYd, heightYd } = PAR_DIMENSIONS[par];
  const w = widthYd * YARD_TO_MM;
  const h = heightYd * YARD_TO_MM;
  const halfW = w / 2;
  return [
    Math.max(-halfW, Math.min(halfW, x)),
    Math.max(0, Math.min(h, y)),
  ];
}

const ANGLE_STEP = Math.PI / 4; // 45°

/** Constrain point to horizontal, vertical, or 45° diagonal from fromPoint. */
function constrainToEightDirections(from: Point, to: Point): Point {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const angle = Math.atan2(dy, dx);
  const snapped = Math.round(angle / ANGLE_STEP) * ANGLE_STEP;
  const cos = Math.cos(snapped);
  const sin = Math.sin(snapped);
  const len = dx * cos + dy * sin;
  return [from[0] + len * cos, from[1] + len * sin];
}

const ERASER_PICK_RADIUS_MM = 2000;
const POINT_MARKER_PICK_RADIUS_MM = 2500;

type HitResult = SelectionHit;

/** Hit-test: find topmost object at point (current hole only). Returns what to remove or null. */
function hitTestAt(point: Point): HitResult | null {
  const [x, y] = point;

  const r2 = ERASER_PICK_RADIUS_MM * ERASER_PICK_RADIUS_MM;
  const pointR2 = POINT_MARKER_PICK_RADIUS_MM * POINT_MARKER_PICK_RADIUS_MM;

  // Point markers (pins, tee positions, 150 yd) — topmost by distance
  for (let i = course.pins.length - 1; i >= 0; i--) {
    const p = course.pins[i];
    if (!p || p.holeNumber !== currentHole) continue;
    const dx = x - p.x;
    const dy = y - p.y;
    if (dx * dx + dy * dy <= pointR2) return { type: 'pin', index: i };
  }
  for (let i = course.teePositions.length - 1; i >= 0; i--) {
    const p = course.teePositions[i];
    if (!p || p.holeNumber !== currentHole) continue;
    const dx = x - p.x;
    const dy = y - p.y;
    if (dx * dx + dy * dy <= pointR2) return { type: 'teePosition', index: i };
  }
  for (let i = course.markers150.length - 1; i >= 0; i--) {
    const p = course.markers150[i];
    if (!p || p.holeNumber !== currentHole) continue;
    const dx = x - p.x;
    const dy = y - p.y;
    if (dx * dx + dy * dy <= pointR2) return { type: 'marker150', index: i };
  }

  // Trees: any tree within radius, current hole only
  for (let i = course.trees.length - 1; i >= 0; i--) {
    const t = course.trees[i];
    if (!t || (t.holeNumber ?? 1) !== currentHole) continue;
    const dx = x - t.x;
    const dy = y - t.y;
    if (dx * dx + dy * dy <= r2) return { type: 'tree', index: i };
  }

  // Houses: any house within radius, current hole only
  for (let i = course.houses.length - 1; i >= 0; i--) {
    const h = course.houses[i];
    if (!h || (h.holeNumber ?? 1) !== currentHole) continue;
    const dx = x - h.x;
    const dy = y - h.y;
    if (dx * dx + dy * dy <= r2) return { type: 'house', index: i };
  }

  // Tee boxes: point in polygon, current hole only
  for (let i = course.tees.length - 1; i >= 0; i--) {
    const tee = course.tees[i];
    if (!tee?.points || tee.holeNumber !== currentHole) continue;
    if (pointInPolygon(point, tee.points)) return { type: 'tee', index: i };
  }

  // Surfaces: reverse draw order, current hole only
  const keysReversed = [...SURFACE_KEYS].reverse();
  for (const key of keysReversed) {
    const shapes = course.surfaces[key];
    if (!shapes) continue;
    for (let si = shapes.length - 1; si >= 0; si--) {
      const shape = shapes[si];
      if (shape?.points && (shape.holeNumber == null || shape.holeNumber === currentHole) && shape.points.length >= 3 && pointInPolygon(point, shape.points)) {
        return { type: 'surface', key, index: si };
      }
    }
  }

  return null;
}

const layerVisible = (layerId: LayerId | null) =>
  layerId === null || layerVisibility[layerId] !== false;

/** Hit-test vertices for reshape tool. Returns closest vertex within radius, topmost shape first. */
function vertexHitTest(point: Point): VertexHit | null {
  const [x, y] = point;
  const r2 = VERTEX_PICK_RADIUS_MM * VERTEX_PICK_RADIUS_MM;
  let best: VertexHit | null = null;
  let bestD2 = r2;

  const checkVertex = (vx: number, vy: number, hit: VertexHit) => {
    const dx = x - vx;
    const dy = y - vy;
    const d2 = dx * dx + dy * dy;
    if (d2 < bestD2) {
      bestD2 = d2;
      best = hit;
    }
  };

  // Tees (drawn on top)
  for (let i = course.tees.length - 1; i >= 0; i--) {
    const tee = course.tees[i];
    if (!tee?.points || tee.holeNumber !== currentHole) continue;
    if (!layerVisible(getLayerForFeature('tees'))) continue;
    for (let vi = 0; vi < tee.points.length; vi++) {
      const p = tee.points[vi];
      if (p) checkVertex(p[0], p[1], { type: 'tee', teeIndex: i, vertexIndex: vi });
    }
  }

  // Surfaces: reverse key order (last drawn = topmost)
  const keysReversed = [...SURFACE_KEYS].reverse();
  for (const key of keysReversed) {
    if (key === 'fringe') continue;
    const layerId = getLayerForSurfaceKey(key);
    if (!layerVisible(layerId)) continue;
    const shapes = course.surfaces[key];
    if (!shapes) continue;
    for (let si = shapes.length - 1; si >= 0; si--) {
      const shape = shapes[si];
      if (!shape?.points || (shape.holeNumber != null && shape.holeNumber !== currentHole)) continue;
      if (shape.points.length < 2) continue;
      if (key === 'secondCut') continue; // base layer cannot be reshaped
      for (let vi = 0; vi < shape.points.length; vi++) {
        const p = shape.points[vi];
        if (p) checkVertex(p[0], p[1], { type: 'surface', key, shapeIndex: si, vertexIndex: vi });
      }
    }
  }

  return best;
}

const MM_TO_YARD = 1 / 914.4;
const MM2_TO_SQ_YARD = 1 / (914.4 * 914.4);

/** Polygon area in mm² (shoelace formula). */
function polygonAreaMm2(points: Point[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    sum += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(sum) * 0.5;
}

/** Polygon perimeter in mm (closed path). */
function polygonPerimeterMm(points: Point[]): number {
  if (points.length < 2) return 0;
  let len = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    len += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return len;
}

/** Path length in mm (open path). */
function pathLengthMm(points: Point[]): number {
  if (points.length < 2) return 0;
  let len = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    len += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return len;
}

const SURFACE_LABELS: Record<SurfaceKey, string> = {
  fairway: 'Fairway',
  firstCut: 'Light Rough',
  secondCut: 'Heavy Rough',
  green: 'Green',
  fringe: 'Fringe',
  bunker: 'Bunker',
  waste: 'Waste',
  water: 'Water',
  cartPath: 'Cart path',
  outOfBounds: 'Out of bounds',
  hazard: 'Hazard',
};

function getMeasurementsForHit(hit: HitResult): string {
  const yd = (mm: number) => (mm * MM_TO_YARD).toFixed(1);
  const sqYd = (mm2: number) => (mm2 * MM2_TO_SQ_YARD).toFixed(1);
  if (hit.type === 'pin') {
    const p = course.pins[hit.index];
    if (!p) return 'Flag';
    return `Flag: (${yd(p.x)}, ${yd(p.y)}) yd`;
  }
  if (hit.type === 'teePosition') {
    const p = course.teePositions[hit.index];
    if (!p) return 'Tee position';
    return `Tee position: (${yd(p.x)}, ${yd(p.y)}) yd`;
  }
  if (hit.type === 'marker150') {
    const p = course.markers150[hit.index];
    if (!p) return '150 yd marker';
    return `150 yd marker: (${yd(p.x)}, ${yd(p.y)}) yd`;
  }
  if (hit.type === 'tree') {
    const t = course.trees[hit.index];
    if (!t) return 'Tree';
    const rYd = (t.radiusMm * MM_TO_YARD).toFixed(1);
    return `Tree: (${yd(t.x)}, ${yd(t.y)}) yd, radius ${rYd} yd`;
  }
  if (hit.type === 'house') {
    const h = course.houses[hit.index];
    if (!h) return 'House';
    const rYd = (h.radiusMm * MM_TO_YARD).toFixed(1);
    return `House: (${yd(h.x)}, ${yd(h.y)}) yd, radius ${rYd} yd`;
  }
  if (hit.type === 'tee') {
    const tee = course.tees[hit.index];
    if (!tee?.points || tee.points.length < 3) return 'Tee box';
    const areaMm2 = polygonAreaMm2(tee.points);
    const perimMm = polygonPerimeterMm(tee.points);
    return `Tee box: ${sqYd(areaMm2)} sq yd, perimeter ${yd(perimMm)} yd`;
  }
  if (hit.type === 'surface') {
    const shape = course.surfaces[hit.key]?.[hit.index];
    if (!shape?.points) return SURFACE_LABELS[hit.key] ?? hit.key;
    const label = SURFACE_LABELS[hit.key] ?? hit.key;
    if (shape.type === 'path') {
      const lenMm = pathLengthMm(shape.points);
      return `${label}: length ${yd(lenMm)} yd`;
    }
    const areaMm2 = polygonAreaMm2(shape.points);
    const perimMm = polygonPerimeterMm(shape.points);
    return `${label}: ${sqYd(areaMm2)} sq yd, perimeter ${yd(perimMm)} yd`;
  }
  return '';
}

function handlePointerDown(e: MouseEvent) {
  if (e.button !== 0) return; // only left-click places or starts actions
  if (!canvasView || !toolbar) return;
  const pt = canvasView.screenToMm(e.clientX, e.clientY);
  const clamped = clampToBounds(pt);
  const tool = toolbar.getActiveTool();

  if (tool === 'pointer') {
    const hit = hitTestAt(clamped);
    selectedHit = hit;
    if (measurementReadout) {
      measurementReadout.textContent = hit ? getMeasurementsForHit(hit) : '';
    }
    redraw();
    return;
  }

  if (tool === 'move') {
    const hit = hitTestAt(clamped);
    if (hit && (hit.type !== 'surface' || hit.key !== 'secondCut')) {
      moveDragHit = hit;
      selectedHit = hit; // show as selected while moving
      moveLastPoint = [clamped[0], clamped[1]];
    }
    redraw();
    return;
  }

  if (tool === 'reshape') {
    const vhit = vertexHitTest(clamped);
    if (vhit) {
      reshapeDragVertex = vhit;
      reshapeLastPoint = [clamped[0], clamped[1]];
    }
    return;
  }

  if (tool === 'duplicate') {
    const hit = hitTestAt(clamped);
    if (hit && (hit.type !== 'surface' || hit.key !== 'secondCut')) {
      let newHit: SelectionHit | null = null;
      if (hit.type === 'tree') {
        const t = course.trees[hit.index];
        if (t) {
          addTree(course, clamped[0], clamped[1], t.type, t.radiusMm, t.holeNumber ?? 1);
          undoStack.push({ type: 'tree', index: course.trees.length - 1 });
          newHit = { type: 'tree', index: course.trees.length - 1 };
        }
      } else if (hit.type === 'house') {
        const h = course.houses[hit.index];
        if (h) {
          addHouse(course, clamped[0], clamped[1], h.radiusMm, h.holeNumber ?? 1);
          undoStack.push({ type: 'house', index: course.houses.length - 1 });
          newHit = { type: 'house', index: course.houses.length - 1 };
        }
      } else if (hit.type === 'tee') {
        const tee = course.tees[hit.index];
        if (tee?.points) {
          const cen = polygonCentroid(tee.points);
          const shiftX = clamped[0] - cen[0];
          const shiftY = clamped[1] - cen[1];
          const pts = tee.points.map((p) => [p[0] + shiftX, p[1] + shiftY] as Point);
          addTee(course, { points: pts, holeNumber: tee.holeNumber ?? 1, label: tee.label });
          undoStack.push({ type: 'tee', index: course.tees.length - 1 });
          newHit = { type: 'tee', index: course.tees.length - 1 };
        }
      } else if (hit.type === 'pin') {
        const p = course.pins[hit.index];
        if (p) {
          addPin(course, { x: clamped[0], y: clamped[1], holeNumber: p.holeNumber, day: p.day });
          undoStack.push({ type: 'pin', index: course.pins.length - 1 });
          newHit = { type: 'pin', index: course.pins.length - 1 };
        }
      } else if (hit.type === 'teePosition') {
        const p = course.teePositions[hit.index];
        if (p) {
          addTeePosition(course, {
            x: clamped[0],
            y: clamped[1],
            holeNumber: p.holeNumber,
            color: p.color,
          });
          undoStack.push({ type: 'teePosition', index: course.teePositions.length - 1 });
          newHit = { type: 'teePosition', index: course.teePositions.length - 1 };
        }
      } else if (hit.type === 'marker150') {
        const m = course.markers150[hit.index];
        if (m) {
          addMarker150(course, { x: clamped[0], y: clamped[1], holeNumber: m.holeNumber });
          undoStack.push({ type: 'marker150', index: course.markers150.length - 1 });
          newHit = { type: 'marker150', index: course.markers150.length - 1 };
        }
      } else if (hit.type === 'surface') {
        const shape = course.surfaces[hit.key]?.[hit.index];
        if (shape?.points) {
          if (hit.key === 'green' && hasGreenForCurrentHole()) return; // only 1 green per hole
          const cen = polygonCentroid(shape.points);
          const shiftX = clamped[0] - cen[0];
          const shiftY = clamped[1] - cen[1];
          const pts = shape.points.map((p) => [p[0] + shiftX, p[1] + shiftY] as Point);
          addSurfaceShape(course, hit.key, {
            type: shape.type ?? 'polygon',
            points: pts,
            holeNumber: shape.holeNumber ?? 1,
          });
          undoStack.push({ type: 'surface', key: hit.key, index: course.surfaces[hit.key].length - 1 });
          newHit = { type: 'surface', key: hit.key, index: course.surfaces[hit.key].length - 1 };
        }
      }
      if (newHit) {
        moveDragHit = newHit;
        selectedHit = newHit;
        moveLastPoint = [clamped[0], clamped[1]];
      }
      redraw();
    }
    return;
  }

  if (tool === 'measure') {
    if (measurePhase === 0) {
      measureAnchor1 = [clamped[0], clamped[1]];
      measurePhase = 1;
      measureLivePoint = [clamped[0], clamped[1]];
    } else if (measurePhase === 1) {
      measureAnchor2 = [clamped[0], clamped[1]];
      measurePhase = 2;
      measureLivePoint = [clamped[0], clamped[1]];
    } else if (measurePhase === 2) {
      measureSegment2End = [clamped[0], clamped[1]];
      measurePhase = 3;
      measureLivePoint = null;
    } else {
      measurePhase = 0;
      measureAnchor1 = null;
      measureAnchor2 = null;
      measureSegment2End = null;
      measureLivePoint = null;
    }
    redraw();
    return;
  }

  if (tool === 'eraser') {
    const hit = hitTestAt(clamped);
    if (hit && (hit.type !== 'surface' || hit.key !== 'secondCut')) {
      cutHit(hit);
      redraw();
    }
    return;
  }

  if (tool === 'pin') {
    if (pointInGreen(clamped, currentHole)) {
      addPin(course, {
        x: clamped[0],
        y: clamped[1],
        holeNumber: currentHole,
        day: toolbar.getPinDay(),
      });
      undoStack.push({ type: 'pin', index: course.pins.length - 1 });
      redraw();
    }
    return;
  }

  if (tool === 'teePosition') {
    if (pointInTeeBox(clamped, currentHole)) {
      addTeePosition(course, {
        x: clamped[0],
        y: clamped[1],
        holeNumber: currentHole,
        color: toolbar.getTeePositionColor(),
      });
      undoStack.push({ type: 'teePosition', index: course.teePositions.length - 1 });
      redraw();
    }
    return;
  }

  if (tool === 'marker150') {
    if (getHolePar(getCourse(), currentHole) === 3) return;
    const pos = getMarker150Position(currentHole);
    if (pos) {
      addMarker150(course, { x: pos[0], y: pos[1], holeNumber: currentHole });
      undoStack.push({ type: 'marker150', index: course.markers150.length - 1 });
      redraw();
    }
    return;
  }

  if (tool === 'tree') {
    addTree(course, clamped[0], clamped[1], '', toolbar.getTreeSizeMm(), currentHole);
    undoStack.push({ type: 'tree', index: course.trees.length - 1 });
    redraw();
    return;
  }

  if (tool === 'house') {
    addHouse(course, clamped[0], clamped[1], toolbar.getHouseSizeMm(), currentHole);
    undoStack.push({ type: 'house', index: course.houses.length - 1 });
    redraw();
    return;
  }

  if (tool === 'elevation') {
    const inches = toolbar.getElevationAmountInches();
    const deltaNormalized = inches * ELEVATION_NORMALIZED_PER_INCH;
    const direction = toolbar.getElevationDirection();
    const delta = direction === 'down' ? -deltaNormalized : deltaNormalized;
    const radiusMm = toolbar.getElevationSizeRadiusMm();
    const edgeMode = toolbar.getElevationEdgeMode();
    addElevationInRegion(course, clamped[0], clamped[1], radiusMm, delta, edgeMode);
    redraw();
    return;
  }

  if (isSurfaceTool(tool) || tool === 'tee') {
    if (tool === 'fairway' && getHolePar(getCourse(), currentHole) === 3) return;
    if (tool === 'green' && hasGreenForCurrentHole()) return;
    isDrawing = true;
    strokePoints = [clamped];
  }
}

const PUSH_EPSILON_MM = 1;

function handlePointerMove(e: MouseEvent) {
  if (!canvasView) return;
  const pt = canvasView.screenToMm(e.clientX, e.clientY);
  const clamped = clampToBounds(pt);

  if (reshapeDragVertex && reshapeLastPoint) {
    const dx = clamped[0] - reshapeLastPoint[0];
    const dy = clamped[1] - reshapeLastPoint[1];
    const v = reshapeDragVertex;
    if (v.type === 'surface') {
      const shape = course.surfaces[v.key]?.[v.shapeIndex];
      const pt = shape?.points[v.vertexIndex];
      if (pt) {
        pt[0] += dx;
        pt[1] += dy;
        touchMetadata(course);
      }
    } else {
      const tee = course.tees[v.teeIndex];
      const pt = tee?.points[v.vertexIndex];
      if (pt) {
        pt[0] += dx;
        pt[1] += dy;
        touchMetadata(course);
      }
    }
    reshapeLastPoint = [clamped[0], clamped[1]];
    redraw();
    return;
  }

  if (moveDragHit && moveLastPoint) {
    const dx = clamped[0] - moveLastPoint[0];
    const dy = clamped[1] - moveLastPoint[1];
    const hit = moveDragHit;
    if (hit.type === 'pin') movePin(course, hit.index, dx, dy);
    else if (hit.type === 'teePosition') moveTeePosition(course, hit.index, dx, dy);
    else if (hit.type === 'marker150') moveMarker150(course, hit.index, dx, dy);
    else if (hit.type === 'tree') moveTree(course, hit.index, dx, dy);
    else if (hit.type === 'house') moveHouse(course, hit.index, dx, dy);
    else if (hit.type === 'tee') moveTee(course, hit.index, dx, dy);
    else if (hit.type === 'surface') moveSurfaceShape(course, hit.key, hit.index, dx, dy);
    moveLastPoint = [clamped[0], clamped[1]];
    redraw();
    return;
  }

  // Pointer tool: update hover hit for selectable outline
  const tool = toolbar?.getActiveTool();
  if (tool === 'pointer' && toolbar) {
    const hit = hitTestAt(clamped);
    if (
      (hit === null) !== (hoverHit === null) ||
      (hit && hoverHit && (hit.type !== hoverHit.type || hit.index !== hoverHit.index || (hit.type === 'surface' && hoverHit.type === 'surface' && hit.key !== hoverHit.key)))
    ) {
      hoverHit = hit;
      redraw();
    }
  } else if (hoverHit) {
    hoverHit = null;
    redraw();
  }

  if (!isDrawing) return;
  let clampedForStroke = clamped;
  const toolForStroke = toolbar!.getActiveTool();

  if (isSurfaceTool(toolForStroke) || toolForStroke === 'tee') {
    if (e.shiftKey && strokePoints.length >= 1) {
      const last = strokePoints[strokePoints.length - 1];
      if (last) clampedForStroke = clampToBounds(constrainToEightDirections(last, clamped));
    }
    const lastPt = strokePoints[strokePoints.length - 1];
    const dist = lastPt
      ? Math.hypot(clampedForStroke[0] - lastPt[0], clampedForStroke[1] - lastPt[1])
      : PUSH_EPSILON_MM + 1;
    if (strokePoints.length === 0 || dist >= PUSH_EPSILON_MM) {
      strokePoints.push(clampedForStroke);
    }
    redraw({ strokePreviewEnd: clampedForStroke });
  }
}

function handlePointerUp(_e: MouseEvent) {
  if (reshapeDragVertex) {
    reshapeDragVertex = null;
    reshapeLastPoint = null;
    redraw();
    return;
  }
  if (moveDragHit) {
    moveDragHit = null;
    moveLastPoint = null;
    // keep selectedHit so item stays selected after move
    redraw();
    return;
  }
  if (!toolbar || !isDrawing) return;
  const tool = toolbar.getActiveTool();

  if (tool === 'tee' && strokePoints.length >= 3) {
    const points = smoothPolyline([...strokePoints], true);
    addTee(course, { points, holeNumber: currentHole });
    undoStack.push({ type: 'tee', index: course.tees.length - 1 });
  } else if (isSurfaceTool(tool) && strokePoints.length >= 2) {
    const shapeType = isPenTool(tool) ? 'path' : 'polygon';
    const raw = [...strokePoints];
    // Green: use fewer segments to avoid Catmull-Rom overshoot → self-intersecting polygon → chaotic lines
    const segments = tool === 'green' ? 2 : 8;
    const points = smoothPolyline(raw, shapeType === 'polygon', segments);
    if (points.length >= (shapeType === 'path' ? 2 : 3)) {
      addSurfaceShape(course, tool, { type: shapeType, points, holeNumber: currentHole });
      undoStack.push({ type: 'surface', key: tool, index: course.surfaces[tool].length - 1 });
      if (tool === 'green') toolbar?.refreshForHoleChange?.();
    }
  }

  strokePoints = [];
  isDrawing = false;
  redraw();
}

function setupCanvas(el: HTMLCanvasElement, coordReadout: HTMLElement) {
  canvasView = createCanvasView(el, getCourse, getCurrentHole, () => layerVisibility, () => showGrid);
  canvasView.draw();

  el.addEventListener('mousedown', handlePointerDown);
  window.addEventListener('mousemove', handlePointerMove);
  window.addEventListener('mouseup', (e: MouseEvent) => handlePointerUp(e));

  el.addEventListener('contextmenu', (e: MouseEvent) => {
    e.preventDefault();
    if (!canvasView) return;
    const pt = canvasView.screenToMm(e.clientX, e.clientY);
    const clamped = clampToBounds(pt);
    const hit = hitTestAt(clamped);
    if (!hit) return;
    if (hit.type === 'surface') {
      if (hit.key === 'secondCut') return; // base layer cannot be sent back
      sendSurfaceShapeToBack(course, hit.key, hit.index);
    } else if (hit.type === 'tee') {
      sendTeeToBack(course, hit.index);
    } else if (hit.type === 'tree') {
      sendTreeToBack(course, hit.index);
    } else if (hit.type === 'house') {
      sendHouseToBack(course, hit.index);
    } else if (hit.type === 'pin') {
      sendPinToBack(course, hit.index);
    } else if (hit.type === 'teePosition') {
      sendTeePositionToBack(course, hit.index);
    } else if (hit.type === 'marker150') {
      sendMarker150ToBack(course, hit.index);
    }
    redraw();
  });

  const eraserCursor =
    "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22%3E%3Cpath fill=%22none%22 stroke=%22%23333%22 stroke-width=%221.5%22 stroke-linecap=%22round%22 d=%22M6 9l6 6M12 9l6 6M9 6v12M15 6v12%22/%3E%3Ccircle fill=%22%23333%22 cx=%229%22 cy=%226%22 r=%221.5%22/%3E%3Ccircle fill=%22%23333%22 cx=%2215%22 cy=%2218%22 r=%221.5%22/%3E%3C/svg%3E') 12 12, auto";

  const elevationCursor =
    "url('data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2224%22 height=%2224%22 viewBox=%220 0 24 24%22%3E%3Cpath fill=%22%236b8e4e%22 stroke=%22%235a7a3d%22 d=%22M4 20 L12 6 L20 20 Z%22/%3E%3C/svg%3E') 12 6, auto";

  el.addEventListener('mousemove', (e: MouseEvent) => {
    if (!canvasView || !toolbar) return;
    const [mx, my] = canvasView.screenToMm(e.clientX, e.clientY);
    const tool = toolbar.getActiveTool();
    const isPointer = tool === 'pointer';
    const isMove = tool === 'move';
    const isReshape = tool === 'reshape';
    const isDuplicate = tool === 'duplicate';
    const isMeasure = tool === 'measure';
    const isEraser = tool === 'eraser';
    const isElevation = tool === 'elevation';
    if (isPointer || isMeasure) el.style.cursor = 'default';
    else if (isMove) el.style.cursor = moveDragHit ? 'grabbing' : 'grab';
    else if (isReshape) el.style.cursor = reshapeDragVertex ? 'grabbing' : 'crosshair';
    else if (isDuplicate || isEraser) el.style.cursor = isEraser ? eraserCursor : 'copy';
    else if (isElevation) el.style.cursor = elevationCursor;
    else el.style.cursor = 'crosshair';
    if (!isPointer) {
      coordReadout.textContent = `X: ${(mx * MM_TO_YARD).toFixed(1)} yd   Y: ${(my * MM_TO_YARD).toFixed(1)} yd`;
    }
    if (isMeasure && (measurePhase === 1 || measurePhase === 2)) {
      const clamped = clampToBounds([mx, my]);
      measureLivePoint = [clamped[0], clamped[1]];
      redraw({ measureLivePoint });
    }
    if (!view3d?.isUserPanning() && !view3d?.isFlyoverRunning()) view3d?.setCameraFromCoursePosition(mx, my);
  });
  el.addEventListener('mouseleave', () => {
    coordReadout.textContent = '';
    if (measurementReadout) measurementReadout.textContent = '';
    el.style.cursor = 'default';
  });
  syncSaveButtonState?.();
}

function setupToolbar(
  el: HTMLElement,
  options?: {
    initialLayer?: LayerId;
    onActiveToolChange?: () => void;
    layerVisibility?: LayerVisibility;
    onLayerVisibilityChange?: (layerId: LayerId, visible: boolean) => void;
    modifiersContainer?: HTMLElement;
    isPar3Hole?: () => boolean;
    hasGreenForCurrentHole?: () => boolean;
  }
) {
  toolbar = createToolbar(el, options);
}

function setupHoleBar(container: HTMLElement) {
  let onHoleChange: (() => void) | undefined;
  const bar = document.createElement('div');
  bar.className = 'hole-bar';
  bar.setAttribute('role', 'group');
  bar.setAttribute('aria-label', 'Select hole');

  const prevBtn = document.createElement('button');
  prevBtn.type = 'button';
  prevBtn.className = 'hole-nav-btn';
  prevBtn.setAttribute('aria-label', 'Previous hole');
  prevBtn.innerHTML = '<i class="fa-solid fa-chevron-left" aria-hidden="true"></i>';

  const holeDisplay = document.createElement('button');
  holeDisplay.type = 'button';
  holeDisplay.className = 'hole-display hole-display-btn';
  holeDisplay.setAttribute('aria-label', 'Select hole');
  holeDisplay.setAttribute('aria-haspopup', 'listbox');
  holeDisplay.setAttribute('aria-expanded', 'false');

  const holePickerOverlay = document.createElement('div');
  holePickerOverlay.className = 'hole-picker-overlay';
  holePickerOverlay.setAttribute('role', 'dialog');
  holePickerOverlay.setAttribute('aria-modal', 'true');
  holePickerOverlay.setAttribute('aria-label', 'Select hole');
  holePickerOverlay.innerHTML = '<div class="hole-picker-content"></div>';
  document.body.appendChild(holePickerOverlay);

  const holePickerContent = holePickerOverlay.querySelector('.hole-picker-content') as HTMLElement;

  function openHolePicker(): void {
    const holeCount = getCourse().metadata.holeCount === 9 ? 9 : 18;
    holePickerContent.innerHTML = '';
    for (let n = 1; n <= holeCount; n++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hole-picker-btn';
      btn.textContent = String(n);
      btn.setAttribute('aria-label', `Hole ${n}`);
      if (n === currentHole) btn.classList.add('active');
      btn.addEventListener('click', () => {
        currentHole = n;
        updateHoleDisplay();
        onHoleChange?.();
        redraw();
        closeHolePicker();
      });
      holePickerContent.appendChild(btn);
    }
    const rect = holeDisplay.getBoundingClientRect();
    holePickerOverlay.classList.add('visible');
    holePickerContent.style.left = `${rect.left}px`;
    holePickerContent.style.top = `${rect.bottom + 4}px`;
    holeDisplay.setAttribute('aria-expanded', 'true');
  }

  function closeHolePicker(): void {
    holePickerOverlay.classList.remove('visible');
    holeDisplay.setAttribute('aria-expanded', 'false');
  }

  holeDisplay.addEventListener('click', () => {
    if (holePickerOverlay.classList.contains('visible')) {
      closeHolePicker();
    } else {
      openHolePicker();
    }
  });

  holePickerOverlay.addEventListener('click', (e) => {
    if (e.target === holePickerOverlay) closeHolePicker();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && holePickerOverlay.classList.contains('visible')) {
      closeHolePicker();
    }
  });

  const nextBtn = document.createElement('button');
  nextBtn.type = 'button';
  nextBtn.className = 'hole-nav-btn';
  nextBtn.setAttribute('aria-label', 'Next hole');
  nextBtn.innerHTML = '<i class="fa-solid fa-chevron-right" aria-hidden="true"></i>';

  function updateHoleDisplay(): void {
    const holeCount = getCourse().metadata.holeCount === 9 ? 9 : 18;
    if (currentHole > holeCount) currentHole = holeCount;
    holeDisplay.textContent = `Hole ${currentHole}`;
    prevBtn.disabled = currentHole <= 1;
    nextBtn.disabled = currentHole >= holeCount;
  }

  prevBtn.addEventListener('click', () => {
    if (currentHole <= 1) return;
    currentHole--;
    updateHoleDisplay();
    onHoleChange?.();
    redraw();
  });

  nextBtn.addEventListener('click', () => {
    const holeCount = getCourse().metadata.holeCount === 9 ? 9 : 18;
    if (currentHole >= holeCount) return;
    currentHole++;
    updateHoleDisplay();
    onHoleChange?.();
    redraw();
  });

  updateHoleDisplay();
  bar.appendChild(prevBtn);
  bar.appendChild(holeDisplay);
  bar.appendChild(nextBtn);
  container.appendChild(bar);

  return {
    registerOnHoleChange: (cb: () => void) => {
      onHoleChange = cb;
    },
    refreshHoleButtons: () => {
      updateHoleDisplay();
      onHoleChange?.();
      redraw();
    },
  };
}

function setupLayerMenu(container: HTMLElement): {
  updateOtherToolsActiveState: () => void;
  switchToLayer: (layerId: LayerId) => void;
  refreshHoleNameInput: () => void;
  refreshHoleButtons: () => void;
  getOnFlyoverChange: () => (running: boolean) => void;
} {
  const bar = document.createElement('div');
  bar.className = 'layer-menu';
  bar.setAttribute('role', 'tablist');
  bar.setAttribute('aria-label', 'Tools and options');

  const sep = document.createElement('span');
  sep.className = 'layer-menu-sep';
  sep.setAttribute('aria-hidden', 'true');
  bar.appendChild(sep);

  const iconTools: { id: ToolId; icon: string; title: string }[] = [
    { id: 'pointer', icon: 'fa-solid fa-hand-pointer', title: 'Pointer' },
    { id: 'move', icon: 'fa-solid fa-up-down-left-right', title: 'Move' },
    { id: 'reshape', icon: 'fa-solid fa-draw-polygon', title: 'Reshape' },
    { id: 'duplicate', icon: 'fa-solid fa-copy', title: 'Duplicate' },
    { id: 'eraser', icon: 'fa-solid fa-scissors', title: 'Cut' },
    { id: 'elevation', icon: 'fa-solid fa-mountain-sun', title: 'Elevation' },
    { id: 'measure', icon: 'fa-solid fa-location-dot', title: 'Location' },
  ];
  const otherToolBtns: HTMLButtonElement[] = [];

  function updateOtherToolsActiveState() {
    const active = toolbar?.getActiveTool();
    otherToolBtns.forEach((b) => {
      const isActive = b.getAttribute('data-tool') === active;
      b.classList.toggle('active', isActive);
      b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  for (const { id, icon, title } of iconTools) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'layer-menu-other-btn';
    btn.setAttribute('data-tool', id);
    btn.setAttribute('aria-label', title);
    btn.setAttribute('title', title);
    btn.setAttribute('aria-pressed', 'false');
    const i = document.createElement('i');
    i.className = icon;
    i.setAttribute('aria-hidden', 'true');
    btn.appendChild(i);
    btn.addEventListener('click', () => {
      selectedHit = null;
      hoverHit = null;
      toolbar?.setActiveTool(id);
      updateOtherToolsActiveState();
    });
    bar.appendChild(btn);
    otherToolBtns.push(btn);
    if (id === 'eraser') {
      const undoBtn = document.createElement('button');
      undoBtn.type = 'button';
      undoBtn.className = 'layer-menu-other-btn';
      undoBtn.setAttribute('aria-label', 'Undo');
      undoBtn.setAttribute('title', 'Undo (Z)');
      undoBtn.setAttribute('aria-pressed', 'false');
      const undoIcon = document.createElement('i');
      undoIcon.className = 'fa-solid fa-rotate-left';
      undoIcon.setAttribute('aria-hidden', 'true');
      undoBtn.appendChild(undoIcon);
      undoBtn.addEventListener('click', () => performUndo());
      bar.appendChild(undoBtn);
    }
  }

  const gridSep = document.createElement('span');
  gridSep.className = 'layer-menu-sep';
  gridSep.setAttribute('aria-hidden', 'true');
  bar.appendChild(gridSep);

  const gridToggle = document.createElement('button');
  gridToggle.type = 'button';
  gridToggle.className = 'layer-menu-other-btn';
  gridToggle.setAttribute('aria-label', 'Toggle grid');
  gridToggle.setAttribute('title', 'Toggle grid');
  gridToggle.setAttribute('aria-pressed', showGrid ? 'true' : 'false');
  if (showGrid) gridToggle.classList.add('active');
  const gridIcon = document.createElement('i');
  gridIcon.className = 'fa-solid fa-th-large';
  gridIcon.setAttribute('aria-hidden', 'true');
  gridToggle.appendChild(gridIcon);
  gridToggle.addEventListener('click', () => {
    showGrid = !showGrid;
    gridToggle.classList.toggle('active', showGrid);
    gridToggle.setAttribute('aria-pressed', showGrid ? 'true' : 'false');
    redraw();
  });
  bar.appendChild(gridToggle);

  const flyoverBtn = document.createElement('button');
  flyoverBtn.type = 'button';
  flyoverBtn.className = 'layer-menu-other-btn';
  flyoverBtn.setAttribute('aria-label', '3D flyover');
  flyoverBtn.setAttribute('title', '3D flyover (Q)');
  flyoverBtn.setAttribute('aria-pressed', 'false');
  const flyoverIcon = document.createElement('i');
  flyoverIcon.className = 'fa-solid fa-helicopter';
  flyoverIcon.setAttribute('aria-hidden', 'true');
  flyoverBtn.appendChild(flyoverIcon);
  flyoverBtn.addEventListener('click', () => view3d?.startFlyover());
  bar.appendChild(flyoverBtn);

  const resetCameraBtn = document.createElement('button');
  resetCameraBtn.type = 'button';
  resetCameraBtn.className = 'layer-menu-other-btn';
  resetCameraBtn.setAttribute('aria-label', 'Reset 3D camera');
  resetCameraBtn.setAttribute('title', 'Reset 3D camera (V)');
  const resetCameraIcon = document.createElement('i');
  resetCameraIcon.className = 'fa-solid fa-camera';
  resetCameraIcon.setAttribute('aria-hidden', 'true');
  resetCameraBtn.appendChild(resetCameraIcon);
  resetCameraBtn.addEventListener('click', () => view3d?.resetCamera());
  bar.appendChild(resetCameraBtn);

  const getOnFlyoverChange = () => (running: boolean) => {
    flyoverBtn.classList.toggle('active', running);
    flyoverBtn.setAttribute('aria-pressed', running ? 'true' : 'false');
  };

  const helpBtn = document.createElement('button');
  helpBtn.type = 'button';
  helpBtn.className = 'layer-menu-other-btn';
  helpBtn.setAttribute('aria-label', 'Keyboard shortcuts');
  helpBtn.setAttribute('title', 'Keyboard shortcuts');
  const helpIcon = document.createElement('i');
  helpIcon.className = 'fa-solid fa-circle-question';
  helpIcon.setAttribute('aria-hidden', 'true');
  helpBtn.appendChild(helpIcon);

  const helpOverlay = document.createElement('div');
  helpOverlay.className = 'help-overlay';
  helpOverlay.setAttribute('role', 'dialog');
  helpOverlay.setAttribute('aria-modal', 'true');
  helpOverlay.setAttribute('aria-labelledby', 'help-overlay-title');
  helpOverlay.innerHTML = `
    <div class="help-overlay-content">
      <div class="help-overlay-header">
        <h2 id="help-overlay-title">Keyboard Shortcuts</h2>
        <button type="button" class="help-overlay-close" aria-label="Close">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>
      <div class="help-overlay-body">
        <h3>General</h3>
        <dl class="help-shortcuts">
          <dt>Ctrl/Cmd+Z</dt><dd>Undo</dd>
          <dt>Ctrl/Cmd+X</dt><dd>Cut</dd>
          <dt>Ctrl/Cmd+C</dt><dd>Copy</dd>
          <dt>Ctrl/Cmd+V</dt><dd>Paste</dd>
          <dt>Delete</dt><dd>Cut selected</dd>
          <dt>3</dt><dd>3D flyover</dd>
          <dt>V</dt><dd>Reset 3D camera</dd>
        </dl>
        <h3>Tools</h3>
        <dl class="help-shortcuts">
          <dt>1</dt><dd>150 marker</dd>
          <dt>P</dt><dd>Pointer</dd>
          <dt>E</dt><dd>Elevation</dd>
          <dt>D</dt><dd>Delete (Eraser)</dd>
          <dt>M</dt><dd>Measure</dd>
          <dt>F</dt><dd>Fairway / Flag (toggle)</dd>
          <dt>T</dt><dd>Tee / Tee position / Tree (cycle)</dd>
          <dt>G</dt><dd>Green</dd>
          <dt>W</dt><dd>Water / Waste (toggle)</dd>
          <dt>B</dt><dd>Bunker</dd>
          <dt>O</dt><dd>Out of bounds</dd>
          <dt>C</dt><dd>Cart path</dd>
          <dt>R</dt><dd>Light Rough / Heavy Rough (toggle)</dd>
          <dt>H</dt><dd>Hazard / House (cycle)</dd>
        </dl>
        <h3>3D view</h3>
        <dl class="help-shortcuts">
          <dt>Left drag</dt><dd>Pan</dd>
          <dt>Right drag</dt><dd>Orbit</dd>
          <dt>Scroll</dt><dd>Zoom</dd>
        </dl>
      </div>
    </div>
  `;
  document.body.appendChild(helpOverlay);

  const helpContent = helpOverlay.querySelector('.help-overlay-content') as HTMLElement;

  const closeHelp = () => {
    helpOverlay.classList.remove('visible');
    helpBtn.focus();
  };

  helpBtn.addEventListener('click', () => {
    if (helpContent) {
      helpContent.classList.remove('dragging');
      helpContent.style.left = '';
      helpContent.style.top = '';
    }
    helpOverlay.classList.add('visible');
    (helpOverlay.querySelector('.help-overlay-close') as HTMLElement)?.focus();
  });
  helpOverlay.querySelector('.help-overlay-close')?.addEventListener('click', closeHelp);

  const helpHeader = helpOverlay.querySelector('.help-overlay-header') as HTMLElement;
  if (helpContent && helpHeader) {
    helpHeader.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      const rect = helpContent.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      helpContent.classList.add('dragging');
      helpContent.style.left = `${rect.left}px`;
      helpContent.style.top = `${rect.top}px`;

      function onMove(ev: MouseEvent) {
        helpContent.style.left = `${ev.clientX - startX}px`;
        helpContent.style.top = `${ev.clientY - startY}px`;
      }
      function onUp() {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      }
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    });
  }

  bar.appendChild(helpBtn);

  const holeNameSep = document.createElement('span');
  holeNameSep.className = 'layer-menu-sep';
  holeNameSep.setAttribute('aria-hidden', 'true');
  bar.appendChild(holeNameSep);

  const holeBarResult = setupHoleBar(bar);

  const parSelect = document.createElement('select');
  parSelect.className = 'hole-par-select';
  parSelect.setAttribute('aria-label', 'Hole par');
  parSelect.title = 'Par for this hole';
  for (const p of [3, 4, 5] as const) {
    const opt = document.createElement('option');
    opt.value = String(p);
    opt.textContent = `Par ${p}`;
    parSelect.appendChild(opt);
  }
  parSelect.value = String(getHolePar(getCourse(), currentHole));
  parSelect.addEventListener('change', () => {
    const par = Number(parSelect.value) as 3 | 4 | 5;
    setHolePar(getCourse(), currentHole, par);
    toolbar?.refreshForHoleChange?.();
    redraw();
    view3d?.updateCourse();
    syncSaveButtonState?.();
  });
  bar.appendChild(parSelect);

  const holeNameInput = document.createElement('input');
  holeNameInput.type = 'text';
  holeNameInput.className = 'hole-name-input';
  holeNameInput.setAttribute('aria-label', 'Hole name');
  holeNameInput.placeholder = 'Hole name';
  holeNameInput.value = getHoleName(currentHole);
  holeNameInput.addEventListener('change', () => {
    const name = holeNameInput.value.trim();
    setHoleName(currentHole, name || `Hole ${currentHole}`);
    holeNameInput.value = getHoleName(currentHole);
  });
  bar.appendChild(holeNameInput);

  function refreshHoleNameInput() {
    holeNameInput.value = getHoleName(currentHole);
    parSelect.value = String(getHolePar(getCourse(), currentHole));
  }
  holeBarResult.registerOnHoleChange(refreshHoleNameInput);

  container.appendChild(bar);

  function switchToLayer(layerId: LayerId) {
    selectedLayer = layerId;
    toolbar?.showLayer(layerId);
    updateOtherToolsActiveState();
  }

  return {
    updateOtherToolsActiveState,
    switchToLayer,
    refreshHoleNameInput: () => {
      refreshHoleNameInput();
      toolbar?.refreshForHoleChange?.();
    },
    refreshHoleButtons: holeBarResult.refreshHoleButtons,
    getOnFlyoverChange,
  };
}

export async function initBuilder() {
  await initClipper();
  const app = document.getElementById('app');
  if (!app) return;
  app.className = 'builder-app';

  let refreshHoleNameInput: (() => void) | undefined;
  let refreshHoleButtons: (() => void) | undefined;
  let loadedFileNameEl: HTMLSpanElement | undefined;
  let courseNameInput: HTMLInputElement | undefined;
  let holeCountSelect: HTMLSelectElement | undefined;
  let lastSavedCourseSnapshot = JSON.stringify(toJSON(getCourse()));
  onMetadataTouched(() => syncSaveButtonState?.());

  function hasUnsavedCourseChanges(): boolean {
    return JSON.stringify(toJSON(getCourse())) !== lastSavedCourseSnapshot;
  }

  const header = document.createElement('header');
  header.className = 'app-header';
  const headerLeft = document.createElement('div');
  headerLeft.className = 'header-left';
  const homeLink = document.createElement('a');
  homeLink.className = 'header-home-link';
  homeLink.href = '/';
  homeLink.setAttribute('aria-label', 'Back to home');
  homeLink.innerHTML =
    '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3 11.5L12 4l9 7.5v8.5h-6v-5h-6v5H3z"/></svg>';
  homeLink.addEventListener('click', (event) => {
    if (!hasUnsavedCourseChanges()) return;
    const shouldLeave = window.confirm(
      'You have unsaved course changes. Click Cancel to save JSON first, or OK to leave without saving.'
    );
    if (!shouldLeave) event.preventDefault();
  });
  const title = document.createElement('h1');
  title.textContent = 'GSPro Course Builder';
  headerLeft.appendChild(homeLink);
  headerLeft.appendChild(title);
  header.appendChild(headerLeft);

  const headerCenter = document.createElement('div');
  headerCenter.className = 'header-center';
  courseNameInput = document.createElement('input');
  courseNameInput.type = 'text';
  courseNameInput.className = 'course-name-input';
  courseNameInput.setAttribute('aria-label', 'Course name');
  courseNameInput.placeholder = 'Untitled';
  courseNameInput.value = getCourse().metadata.name || '';
  courseNameInput.addEventListener('change', () => {
    const name = courseNameInput!.value.trim();
    touchMetadata(getCourse(), { name: name || 'Untitled' });
    syncSaveButtonState?.();
  });
  holeCountSelect = document.createElement('select');
  holeCountSelect.className = 'hole-count-select';
  holeCountSelect.setAttribute('aria-label', 'Hole count');
  const option9 = document.createElement('option');
  option9.value = '9';
  option9.textContent = '9 holes';
  holeCountSelect.appendChild(option9);
  const option18 = document.createElement('option');
  option18.value = '18';
  option18.textContent = '18 holes';
  holeCountSelect.appendChild(option18);
  holeCountSelect.value = String(getCourse().metadata.holeCount);
  holeCountSelect.addEventListener('change', () => {
    const holeCount: 9 | 18 = holeCountSelect!.value === '9' ? 9 : 18;
    const c = getCourse();
    touchMetadata(c, { holeCount });
    applyDimensionsFromPars(c);
    refreshHoleButtons?.();
    syncSaveButtonState?.();
    redraw();
    view3d?.updateCourse();
  });
  loadedFileNameEl = document.createElement('span');
  loadedFileNameEl.className = 'loaded-filename';
  loadedFileNameEl.textContent = '';
  headerCenter.appendChild(courseNameInput);
  headerCenter.appendChild(holeCountSelect);
  headerCenter.appendChild(loadedFileNameEl);
  header.appendChild(headerCenter);

  const headerActions = document.createElement('div');
  headerActions.className = 'header-actions';
  const loadInput = document.createElement('input');
  loadInput.type = 'file';
  loadInput.accept = '.json,application/json';
  loadInput.className = 'header-file-input';
  loadInput.setAttribute('aria-label', 'Load JSON');
  let loadedFileName: string | null = null;

  loadInput.addEventListener('change', () => {
    const file = loadInput.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        course = fromJSON(parsed);
        loadedFileName = file.name;
        if (loadedFileNameEl) loadedFileNameEl.textContent = loadedFileName;
        if (courseNameInput) courseNameInput.value = getCourse().metadata.name || '';
        if (holeCountSelect) holeCountSelect.value = String(getCourse().metadata.holeCount);
        undoStack.length = 0;
        redraw();
        view3d?.updateCourse();
        refreshHoleNameInput?.();
        refreshHoleButtons?.();
        lastSavedCourseSnapshot = JSON.stringify(toJSON(getCourse()));
        syncSaveButtonState?.();
      } catch (err) {
        alert('Failed to load: ' + (err instanceof Error ? err.message : 'Invalid JSON'));
      }
      loadInput.value = '';
    };
    reader.readAsText(file);
  });

  const loadBtn = document.createElement('button');
  loadBtn.type = 'button';
  loadBtn.className = 'header-save-btn';
  loadBtn.textContent = 'Load';
  loadBtn.addEventListener('click', () => loadInput.click());
  headerActions.appendChild(loadBtn);

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.className = 'header-save-btn';
  saveBtn.textContent = 'Save';
  const revertBtn = document.createElement('button');
  revertBtn.type = 'button';
  revertBtn.className = 'header-save-btn';
  revertBtn.textContent = 'Revert';
  function updateSaveButtonState(): void {
    const hasUnsaved = hasUnsavedCourseChanges();
    saveBtn.disabled = !hasUnsaved;
    revertBtn.disabled = !hasUnsaved;
  }
  syncSaveButtonState = updateSaveButtonState;
  saveBtn.addEventListener('click', () => {
    const savedCopy = toJSON(getCourse());
    const json = JSON.stringify(savedCopy, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const name = getCourse().metadata.name.replace(/[^a-zA-Z0-9-_]/g, '_') || 'course';
    a.download = `${name}.json`;
    a.click();
    URL.revokeObjectURL(url);
    lastSavedCourseSnapshot = JSON.stringify(savedCopy);
    updateSaveButtonState();
  });
  const revertConfirmOverlay = document.createElement('div');
  revertConfirmOverlay.className = 'confirm-overlay';
  revertConfirmOverlay.innerHTML = `
    <div class="confirm-overlay-content" role="dialog" aria-modal="true" aria-labelledby="revert-confirm-title">
      <h2 id="revert-confirm-title">Revert unsaved changes?</h2>
      <p>This will discard all unsaved course changes and restore the last saved state.</p>
      <div class="confirm-overlay-actions">
        <button type="button" class="header-save-btn confirm-cancel-btn">Cancel</button>
        <button type="button" class="header-save-btn confirm-revert-btn">Revert</button>
      </div>
    </div>
  `;
  document.body.appendChild(revertConfirmOverlay);
  const revertConfirmCancelBtn = revertConfirmOverlay.querySelector('.confirm-cancel-btn') as HTMLButtonElement;
  const revertConfirmRevertBtn = revertConfirmOverlay.querySelector('.confirm-revert-btn') as HTMLButtonElement;

  function closeRevertConfirm(): void {
    revertConfirmOverlay.classList.remove('visible');
    revertBtn.focus();
  }

  function applyRevertToLastSaved(): void {
    course = fromJSON(JSON.parse(lastSavedCourseSnapshot));
    undoStack.length = 0;
    selectedHit = null;
    hoverHit = null;
    moveDragHit = null;
    moveLastPoint = null;
    reshapeDragVertex = null;
    reshapeLastPoint = null;
    if (courseNameInput) courseNameInput.value = getCourse().metadata.name || '';
    if (holeCountSelect) holeCountSelect.value = String(getCourse().metadata.holeCount);
    refreshHoleNameInput?.();
    refreshHoleButtons?.();
    redraw();
    view3d?.updateCourse();
    updateSaveButtonState();
  }

  revertConfirmCancelBtn.addEventListener('click', closeRevertConfirm);
  revertConfirmRevertBtn.addEventListener('click', () => {
    closeRevertConfirm();
    applyRevertToLastSaved();
  });
  revertConfirmOverlay.addEventListener('click', (event) => {
    if (event.target === revertConfirmOverlay) closeRevertConfirm();
  });
  window.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && revertConfirmOverlay.classList.contains('visible')) {
      event.preventDefault();
      closeRevertConfirm();
    }
  });
  revertBtn.addEventListener('click', () => {
    if (revertBtn.disabled) return;
    revertConfirmOverlay.classList.add('visible');
    revertConfirmCancelBtn.focus();
  });
  headerActions.appendChild(saveBtn);
  headerActions.appendChild(revertBtn);
  updateSaveButtonState();

  header.appendChild(headerActions);
  header.appendChild(loadInput);

  const holeBarWrap = document.createElement('div');
  holeBarWrap.className = 'hole-bar-wrap';

  const layerMenuResult = setupLayerMenu(holeBarWrap);
  refreshHoleNameInput = layerMenuResult.refreshHoleNameInput;
  refreshHoleButtons = layerMenuResult.refreshHoleButtons;
  const { updateOtherToolsActiveState, switchToLayer } = layerMenuResult;

  const main = document.createElement('main');
  main.className = 'app-main';

  const SPLIT_STORAGE_KEY = 'gspro-3d-view-width-pct';
  const SPLIT_DEFAULT_PCT = 66.67;
  const SPLIT_MIN_PCT = 20;
  const SPLIT_MAX_PCT = 80;

  let split3dPct = SPLIT_DEFAULT_PCT;
  try {
    const stored = localStorage.getItem(SPLIT_STORAGE_KEY);
    if (stored != null) {
      const pct = parseFloat(stored);
      if (pct >= SPLIT_MIN_PCT && pct <= SPLIT_MAX_PCT) split3dPct = pct;
    }
  } catch {
    /* ignore */
  }

  const contentArea = document.createElement('div');
  contentArea.className = 'app-content';
  contentArea.style.setProperty('--split-3d-pct', String(split3dPct));

  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'canvas-wrap';
  const canvasModifiersBar = document.createElement('div');
  canvasModifiersBar.className = 'canvas-modifiers-bar';
  canvasWrap.appendChild(canvasModifiersBar);
  const canvasArea = document.createElement('div');
  canvasArea.className = 'canvas-area';
  const canvas = document.createElement('canvas');
  canvas.id = 'course-canvas';
  canvasArea.appendChild(canvas);
  const coordReadout = document.createElement('div');
  coordReadout.className = 'canvas-coord-readout';
  coordReadout.setAttribute('aria-live', 'polite');
  canvasArea.appendChild(coordReadout);
  measurementReadout = document.createElement('div');
  measurementReadout.className = 'canvas-measurement-readout';
  measurementReadout.setAttribute('aria-live', 'polite');
  canvasArea.appendChild(measurementReadout);
  canvasWrap.appendChild(canvasArea);

  const sidebar = document.createElement('aside');
  sidebar.className = 'sidebar';
  const randomizeBtn = document.createElement('button');
  randomizeBtn.type = 'button';
  randomizeBtn.className = 'toolbar-randomize-btn';
  randomizeBtn.textContent = 'Randomize';
  randomizeBtn.setAttribute('aria-label', 'Randomize hole');
  randomizeBtn.setAttribute('title', 'Generate random layout for this hole');
  randomizeBtn.addEventListener('click', () => {
    randomizeHole(getCourse(), currentHole);
    redraw();
    view3d?.updateCourse();
    toolbar?.refreshForHoleChange?.();
  });
  sidebar.appendChild(randomizeBtn);

  const randomizeAllProgressOverlay = document.createElement('div');
  randomizeAllProgressOverlay.className = 'progress-overlay';
  randomizeAllProgressOverlay.setAttribute('aria-live', 'polite');
  randomizeAllProgressOverlay.setAttribute('aria-busy', 'true');
  randomizeAllProgressOverlay.innerHTML = `
    <div class="progress-overlay-content" role="dialog" aria-modal="true" aria-labelledby="progress-overlay-title">
      <div class="progress-overlay-spinner"><i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i></div>
      <h2 id="progress-overlay-title">Randomizing course</h2>
      <p class="progress-overlay-status">Preparing…</p>
    </div>
  `;
  document.body.appendChild(randomizeAllProgressOverlay);
  const progressStatusEl = randomizeAllProgressOverlay.querySelector('.progress-overlay-status') as HTMLElement;

  const randomizeAllBtn = document.createElement('button');
  randomizeAllBtn.type = 'button';
  randomizeAllBtn.className = 'toolbar-randomize-btn';
  randomizeAllBtn.textContent = 'Randomize All';
  randomizeAllBtn.setAttribute('aria-label', 'Randomize all holes');
  randomizeAllBtn.setAttribute('title', 'Generate random layout for all holes (4 par 3, 4 par 5, rest par 4)');
  randomizeAllBtn.addEventListener('click', async () => {
    randomizeAllProgressOverlay.classList.add('visible');
    progressStatusEl.textContent = 'Preparing…';

    await randomizeAllAsync(getCourse(), (current, total) => {
      progressStatusEl.textContent = `Hole ${current} of ${total}…`;
    });

    randomizeAllProgressOverlay.classList.remove('visible');
    redraw();
    view3d?.updateCourse();
    toolbar?.refreshForHoleChange?.();
    refreshHoleNameInput?.();
    refreshHoleButtons?.();
    syncSaveButtonState?.();
  });
  sidebar.appendChild(randomizeAllBtn);
  setupToolbar(sidebar, {
    initialLayer: selectedLayer,
    onActiveToolChange: updateOtherToolsActiveState,
    layerVisibility,
    onLayerVisibilityChange: (layerId, visible) => {
      layerVisibility[layerId] = visible;
      redraw();
      view3d?.updateCourse();
    },
    modifiersContainer: canvasModifiersBar,
    isPar3Hole: () => getHolePar(getCourse(), currentHole) === 3,
    hasGreenForCurrentHole,
  });
  updateOtherToolsActiveState();

  const view3dWrap = document.createElement('div');
  view3dWrap.className = 'view3d-wrap';
  view3dWrap.setAttribute('aria-label', '3D preview');

  const splitter = document.createElement('div');
  splitter.className = 'app-content-splitter';
  splitter.setAttribute('aria-label', 'Resize 2D and 3D views');

  splitter.addEventListener('mousedown', (e: MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startPct = split3dPct;

    function onMove(e: MouseEvent) {
      const rect = contentArea.getBoundingClientRect();
      const contentWidth = rect.width;
      if (contentWidth <= 0) return;
      const deltaX = e.clientX - startX;
      const deltaPct = (deltaX / contentWidth) * 100;
      let newPct = startPct - deltaPct;
      newPct = Math.max(SPLIT_MIN_PCT, Math.min(SPLIT_MAX_PCT, newPct));
      split3dPct = newPct;
      contentArea.style.setProperty('--split-3d-pct', String(newPct));
      try {
        localStorage.setItem(SPLIT_STORAGE_KEY, String(newPct));
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new Event('resize'));
    }

    function onUp() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  });

  contentArea.appendChild(canvasWrap);
  contentArea.appendChild(splitter);
  contentArea.appendChild(view3dWrap);
  main.appendChild(sidebar);
  main.appendChild(contentArea);

  app.appendChild(header);
  app.appendChild(holeBarWrap);
  app.appendChild(main);

  setupCanvas(canvas, coordReadout);
  view3d = createView3d(view3dWrap, getCourse, getCurrentHole, () => layerVisibility, {
    onFlyoverChange: layerMenuResult.getOnFlyoverChange(),
  });

  const TOOL_SHORTCUTS: Record<string, { tool: ToolId; layer?: LayerId }> = {
    d: { tool: 'duplicate' },
    e: { tool: 'elevation' },
    l: { tool: 'measure' },
    m: { tool: 'move' },
    p: { tool: 'pointer' },
    s: { tool: 'reshape' },
    x: { tool: 'eraser' },
    f: { tool: 'fairway', layer: 'playingArea' },
    t: { tool: 'tee', layer: 'tee' },
    g: { tool: 'green', layer: 'green' },
    w: { tool: 'water', layer: 'hazards' },
    b: { tool: 'bunker', layer: 'hazards' },
    o: { tool: 'outOfBounds', layer: 'hazards' },
    c: { tool: 'cartPath', layer: 'playingArea' },
    r: { tool: 'firstCut', layer: 'playingArea' },
  };

  const TOOL_TOGGLES: Record<string, { alt: ToolId; layer: LayerId }> = {
    w: { alt: 'waste', layer: 'hazards' },
    f: { alt: 'pin', layer: 'green' },
    r: { alt: 'secondCut', layer: 'playingArea' },
  };

  const TOOL_CYCLES: Record<string, { tools: [ToolId, ToolId, ...ToolId[]]; layers: LayerId[] }> = {
    t: { tools: ['tee', 'teePosition', 'tree'], layers: ['tee', 'tee', 'hazards'] },
    h: { tools: ['hazard', 'house'], layers: ['hazards', 'hazards'] },
  };

  window.addEventListener('keydown', (e: KeyboardEvent) => {
    const isMod = e.ctrlKey || e.metaKey;
    const target = e.target as HTMLElement;
    const inInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

    // Ctrl/Cmd+Z: undo
    if (isMod && e.key.toLowerCase() === 'z' && !inInput) {
      e.preventDefault();
      performUndo();
      return;
    }
    // Ctrl/Cmd+X: cut
    if (isMod && e.key.toLowerCase() === 'x' && !inInput && selectedHit) {
      const hit = selectedHit;
      if (hit.type !== 'surface' || hit.key !== 'secondCut') {
        cutHit(hit);
      }
      selectedHit = null;
      hoverHit = null;
      e.preventDefault();
      redraw();
      return;
    }
    // Ctrl/Cmd+C: copy
    if (isMod && e.key.toLowerCase() === 'c' && !inInput && selectedHit) {
      copyHitToClipboard(selectedHit);
      e.preventDefault();
      return;
    }
    // Ctrl/Cmd+V: paste
    if (isMod && e.key.toLowerCase() === 'v' && !inInput) {
      if (pasteFromClipboard()) {
        e.preventDefault();
        redraw();
      }
      return;
    }

    if (e.key === '3') {
      e.preventDefault();
      view3d?.startFlyover();
      return;
    }
    if (e.key.toLowerCase() === 'v') {
      e.preventDefault();
      view3d?.resetCamera();
      return;
    }
    // Skip if typing in an input
    if (inInput) return;

    // Escape: switch to Pointer and cancel in-progress actions
    if (e.key === 'Escape' && !revertConfirmOverlay.classList.contains('visible')) {
      moveDragHit = null;
      moveLastPoint = null;
      reshapeDragVertex = null;
      reshapeLastPoint = null;
      if (isDrawing) {
        isDrawing = false;
        strokePoints = [];
      }
      measurePhase = 0;
      measureLivePoint = null;
      measureAnchor1 = null;
      measureAnchor2 = null;
      measureSegment2End = null;
      if (toolbar) {
        toolbar.setActiveTool('pointer');
        updateOtherToolsActiveState();
      }
      e.preventDefault();
      redraw();
      return;
    }

    // Delete/Backspace: cut selected item
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedHit) {
      const hit = selectedHit;
      if (hit.type !== 'surface' || hit.key !== 'secondCut') {
        cutHit(hit);
      }
      selectedHit = null;
      hoverHit = null;
      e.preventDefault();
      redraw();
      return;
    }

    const key = e.key.toLowerCase();
    if (!toolbar) return;

    let tool: ToolId;
    let layer: LayerId | undefined;

    if (key === '1') {
      tool = 'marker150';
      layer = 'playingArea';
    } else {
      const shortcut = TOOL_SHORTCUTS[key];
      const toggle = TOOL_TOGGLES[key];
      const cycle = TOOL_CYCLES[key];
      const current = toolbar.getActiveTool();

      if (cycle) {
        const idx = cycle.tools.indexOf(current);
        const nextIdx = idx >= 0 ? (idx + 1) % cycle.tools.length : 0;
        tool = cycle.tools[nextIdx]!;
        layer = cycle.layers[nextIdx];
      } else if (toggle && shortcut) {
        if (current === shortcut.tool) {
          tool = toggle.alt;
          layer = toggle.layer;
        } else if (current === toggle.alt) {
          tool = shortcut.tool;
          layer = shortcut.layer;
        } else {
          tool = shortcut.tool;
          layer = shortcut.layer;
        }
      } else if (shortcut) {
        tool = shortcut.tool;
        layer = shortcut.layer;
      } else {
        return;
      }
    }

    if ((tool === 'fairway' || tool === 'marker150') && getHolePar(getCourse(), currentHole) === 3) return;

    e.preventDefault();
    selectedHit = null;
    hoverHit = null;
    if (layer) switchToLayer(layer);
    toolbar.setActiveTool(tool);
    updateOtherToolsActiveState();
  });

  window.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key !== 'Shift') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
    if (!toolbar || isDrawing) return;
    toolbar.setActiveTool('move');
    updateOtherToolsActiveState();
    redraw();
  });

  window.addEventListener('keyup', (e: KeyboardEvent) => {
    if (e.key !== 'Shift') return;
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
    if (!toolbar) return;
    toolbar.setActiveTool('pointer');
    updateOtherToolsActiveState();
    redraw();
  });

  requestAnimationFrame(() => {
    redraw();
    view3d?.updateCourse();
  });
}
